/**
 * Touch + mouse signature pad.
 *
 * Captures pointer paths on a canvas and exports a transparent PNG
 * data URL when asked. No external dependency — keeps the bundle
 * tight and avoids pulling signature_pad just for ~80 LOC.
 *
 * The canvas is rendered at 2x its CSS size so the exported PNG is
 * crisp when stamped onto the PDF.
 */
import React, { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';

export interface SignaturePadHandle {
  /** Returns the drawn signature as a PNG data URL, or null if empty. */
  exportPng: () => string | null;
  clear: () => void;
  isEmpty: () => boolean;
}

interface Props {
  width?: number;        // CSS pixels
  height?: number;
  label?: string;
  clearLabel?: string;
}

const SignaturePad = forwardRef<SignaturePadHandle, Props>(({
  width = 480, height = 160, label, clearLabel = 'Clear',
}, ref) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const [empty, setEmpty] = useState(true);

  // Hi-DPI: draw at 2x and scale via CSS so the exported PNG is sharp.
  const dpr = 2;

  useEffect(() => {
    const c = canvasRef.current!;
    c.width = width * dpr;
    c.height = height * dpr;
    const ctx = c.getContext('2d')!;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1a1a1a';
  }, [width, height]);

  const getPos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const c = canvasRef.current!;
    const r = c.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const onDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    drawing.current = true;
    last.current = getPos(e);
    (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
  };

  const onMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current || !last.current) return;
    e.preventDefault();
    const p = getPos(e);
    const ctx = canvasRef.current!.getContext('2d')!;
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (empty) setEmpty(false);
  };

  const onUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    drawing.current = false;
    last.current = null;
  };

  const clear = () => {
    const c = canvasRef.current!;
    const ctx = c.getContext('2d')!;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.restore();
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1a1a1a';
    setEmpty(true);
  };

  useImperativeHandle(ref, () => ({
    exportPng: () => empty ? null : canvasRef.current!.toDataURL('image/png'),
    clear,
    isEmpty: () => empty,
  }), [empty]);

  return (
    <Box>
      {label && (
        <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
          {label}
        </Typography>
      )}
      <Box
        sx={{
          border: '1px dashed',
          borderColor: empty ? 'grey.400' : 'primary.main',
          borderRadius: 1,
          bgcolor: 'background.paper',
          width: 'fit-content',
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            display: 'block',
            width: `${width}px`,
            height: `${height}px`,
            touchAction: 'none',
            cursor: 'crosshair',
          }}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          onPointerLeave={onUp}
        />
      </Box>
      <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
        <Button size="small" onClick={clear} disabled={empty}>{clearLabel}</Button>
      </Stack>
    </Box>
  );
});

SignaturePad.displayName = 'SignaturePad';
export default SignaturePad;
